import { useState, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MaxTabsModal } from './max-tabs-modal.tsx';

interface AddScheduleModalProps {
  children: ReactNode;
  maxTabs: number;
  currentTabCount: number;
  onAddSchedule: (name: string) => void;
}

export function AddScheduleModal({
  children,
  maxTabs,
  currentTabCount,
  onAddSchedule,
}: AddScheduleModalProps) {
  const [open, setOpen] = useState(false);
  const [newScheduleName, setNewScheduleName] = useState('');

  const handleOpenChange = (newOpen: boolean) => {
    if (currentTabCount >= maxTabs && newOpen) {
      return; // Don't open if max tabs reached
    }
    setOpen(newOpen);
    if (!newOpen) {
      setNewScheduleName('');
    }
  };

  const handleAddSchedule = () => {
    if (newScheduleName.trim()) {
      onAddSchedule(newScheduleName);
      setNewScheduleName('');
      setOpen(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setNewScheduleName('');
  };

  if (currentTabCount >= maxTabs) {
    return <MaxTabsModal maxTabs={maxTabs}>{children}</MaxTabsModal>;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Schedule</DialogTitle>
          <DialogDescription>Create a new schedule.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="add-name">Name</Label>
          <Input
            id="add-name"
            value={newScheduleName}
            onChange={(e) => setNewScheduleName(e.target.value)}
            placeholder="Enter schedule name"
            className="col-span-3"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleAddSchedule();
              }
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleAddSchedule} disabled={!newScheduleName.trim()}>
            Create Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
